import { fetchTokenAuth } from "../standardAuthPatterns";
import { defineConnector } from "../types";
import { exactUrlToRegex } from "../urlPatternConversion";
import logo from "./logo.svg";

export const vega = defineConnector({
  identifier: "vega",
  name: "Vega",
  tagLine: "Security analytics, threat detection, and investigation",
  logo,
  protocol: "http",
  tags: ["security"],
  defaultUrlPattern: ({ apiUrl }) => exactUrlToRegex(apiUrl),
  auth: (app) =>
    fetchTokenAuth({
      request: {
        url: `${app.config.apiUrl}/login_machine`,
        method: "POST",
        jsonBody: { access_key: app.secret.accessKey },
      },
      extract: { from: "jsonPath", paths: ["session_jwt"] },
      apply: {
        kind: "header",
        name: "JWTSessionToken",
        extraHeaders: [
          { name: "X-Vega-Key-Id", value: app.secret.accessKeyId },
        ],
      },
    }),
  steps: [
    {
      title: "Create an access key",
      items: [
        {
          kind: "markdown",
          content:
            "Contact [Vega Support](mailto:support@vega.io) to enable API access for your organization. In Vega, go to **Settings > Access keys** and create a key with the roles and scopes your workflows need. Copy the key value and its ID. Vega only shows the key value once. See the [authentication guide](https://docs.vega.io/api-docs/authentication).",
        },
      ],
    },
    {
      title: "Enter your Vega details",
      items: [
        {
          kind: "select",
          label: "Region",
          required: true,
          defaultValue: "https://api.vega.io/api/v1",
          options: [
            { value: "https://api.vega.io/api/v1", label: "US" },
            { value: "https://api.eu.vega.io/api/v1", label: "EU" },
          ],
          helperText:
            "Select the region where your Vega organization is hosted.",
          target: { kind: "configuration", field: "apiUrl" },
        },
        {
          kind: "input",
          label: "Access key",
          required: true,
          target: { kind: "secrets", field: "accessKey" },
        },
        {
          kind: "input",
          label: "Access key ID",
          required: true,
          helperText: "Copy the key ID from Settings > Access keys.",
          target: { kind: "secrets", field: "accessKeyId" },
        },
      ],
    },
  ],
  connectionTest: {
    protocol: "http",
    request: ({ apiUrl }) => ({
      url: `${apiUrl}/query`,
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ query: "{ __typename }" }),
      expectedBodyContains: ['"data"', '"__typename"'],
    }),
  },
  publicEnvVars: ({ apiUrl }) => ({ VEGA_URL: apiUrl }),
});
